<template>
  <div class="media-input">
    <input
      type="file"
      accept="image/png, image/jpeg, image/heic"
      ref="imgInputRef"
      @change="changeImages"
      multiple
      style="display: none"
    />

    <input
      type="file"
      accept="video/mp4,video/webm"
      ref="videoInputRef"
      @change="changeVideos"
      multiple
      style="display: none"
    />

    <!-- Sección FOTOS -->
    <div class="section">
      <h3 class="section__title"><font-awesome-icon icon="image" /> Fotos</h3>

      <!-- Zona de drop y botón añadir -->
      <div
        class="drop-zone"
        :class="{ 'drop-zone--dragover': isDragging }"
        @click="triggerFileInput"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div class="drop-zone__content">
          <font-awesome-icon class="drop-zone__icon" icon="plus" />
          <p class="drop-zone__text">Afegir fotos</p>
          <p class="drop-zone__hint">
            Arrossega fotos aquí o clica per seleccionar
          </p>
        </div>
      </div>

      <div class="items-list">
        <div
          v-for="(img, index) in images"
          :key="'img-' + index"
          class="image-item"
        >
          <div class="image-item__preview">
            <img :src="img.base64" style="max-width: 100%; max-height: 150px" />
          </div>
          <div class="image-item__info">
            <p class="image-item__filename">{{ img.filename }}</p>
            <textarea
              v-model="img.missatge"
              class="form-input"
              rows="3"
              placeholder="MISSATGE"
            ></textarea>
            <input
              v-model="img.signatura"
              class="form-input"
              type="text"
              placeholder="SIGNATURA"
            />
            <div class="image-item__actions">
              <button @click.stop="rotateLeft(index)"><font-awesome-icon icon="rotate-left" /> Girar</button>
              <button @click.stop="removeImage(index)" class="remove-btn">
                <font-awesome-icon icon="x" /> Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección VIDEOS -->
    <div class="section">
      <h3 class="section__title"><font-awesome-icon icon="video" /> Videos</h3>

      <div
        class="drop-zone drop-zone--video"
        :class="{ 'drop-zone--dragover': isDraggingVideo }"
        @click="triggerVideoInput"
        @dragover.prevent="onDragOverVideo"
        @dragleave.prevent="onDragLeaveVideo"
        @drop.prevent="onDropVideo"
      >
        <div class="drop-zone__content">
          <font-awesome-icon class="drop-zone__icon" icon="plus" />
          <p class="drop-zone__text">Afegir videos</p>
          <p class="drop-zone__hint">
            Arrossega videos aquí o clica per seleccionar (MP4, WebM)
          </p>
        </div>
      </div>

      <div class="items-list">
        <div
          v-for="(video, index) in videos"
          :key="'video-' + index"
          class="video-item"
        >
          <div class="video-item__preview">
            <video
              :src="video.base64"
              style="max-width: 100%; max-height: 150px"
            />
            <div class="video-item__badge">VIDEO</div>
          </div>
          <div class="video-item__info">
            <p class="video-item__filename">{{ video.filename }}</p>
            <textarea
              v-model="video.missatge"
              class="form-input"
              rows="3"
              placeholder="MISSATGE"
            ></textarea>
            <input
              v-model="video.signatura"
              class="form-input"
              type="text"
              placeholder="SIGNATURA"
            />
            <div class="video-item__actions">
              <button @click.stop="removeVideo(index)" class="remove-btn">
                <font-awesome-icon icon="x" /> Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección TEXTOS -->
    <div class="section">
      <h3 class="section__title"><font-awesome-icon icon="pen" /> Textos</h3>

      <!-- Botón añadir texto -->
      <div class="drop-zone drop-zone--text" @click="addTextEntry">
        <div class="drop-zone__content">
          <font-awesome-icon class="drop-zone__icon" icon="plus" />
          <p class="drop-zone__text">Afegir text</p>
          <p class="drop-zone__hint">Comparteix un poema, una anècdota, un record o unes paraules de comiat</p>
        </div>
      </div>

      <div class="items-list">
        <div
          v-for="(text, index) in texts"
          :key="'text-' + index"
          class="text-item"
        >
          <div class="text-item__info">
            <textarea
              v-model="text.missatge"
              class="form-input"
              rows="3"
              placeholder="MISSATGE"
            ></textarea>
            <input
              v-model="text.signatura"
              class="form-input"
              type="text"
              placeholder="SIGNATURA"
            />
            <div class="text-item__ideas">
              <span class="idea-tag">poema</span>
              <span class="idea-tag">anècdota</span>
              <span class="idea-tag">record</span>
              <span class="idea-tag">reflexió</span>
              <span class="idea-tag">comiat</span>
            </div>
            <div class="text-item__actions">
              <button @click.stop="removeText(index)" class="remove-btn">
                <font-awesome-icon icon="x" /> Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

interface ImageEntry {
  file: File;
  filename: string;
  base64: string;
  originalBase64: string;
  rotation: number;
  missatge: string;
  signatura: string;
}

interface VideoEntry {
  file: File;
  filename: string;
  base64: string;
  missatge: string;
  signatura: string;
}

interface TextEntry {
  missatge: string;
  signatura: string;
}

export default defineComponent({
  props: {
    idVetlla: {
      type: Number,
      required: true,
    },
  },
  emits: ["update:count"],
  setup(props, { emit }) {
    const imgInputRef = ref<HTMLInputElement | null>(null);
    const videoInputRef = ref<HTMLInputElement | null>(null);
    const images = ref<ImageEntry[]>([]);
    const videos = ref<VideoEntry[]>([]);
    const texts = ref<TextEntry[]>([]);
    const isDragging = ref(false);
    const isDraggingVideo = ref(false);

    const triggerFileInput = () => {
      imgInputRef.value?.click();
    };

    const triggerVideoInput = () => {
      videoInputRef.value?.click();
    };

    const onDragOver = () => {
      isDragging.value = true;
    };

    const onDragLeave = () => {
      isDragging.value = false;
    };

    const onDragOverVideo = () => {
      isDraggingVideo.value = true;
    };

    const onDragLeaveVideo = () => {
      isDraggingVideo.value = false;
    };

    const onDrop = (e: DragEvent) => {
      isDragging.value = false;
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleFiles(files);
      }
    };

    const onDropVideo = (e: DragEvent) => {
      isDraggingVideo.value = false;
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleVideoFiles(files);
      }
    };

    const handleFiles = async (files: FileList) => {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith("image/")) {
          const base64 = await blobToDataUrl(file);
          images.value.push({
            file: file,
            filename: file.name,
            base64: base64,
            originalBase64: base64,
            rotation: 0,
            missatge: "",
            signatura: "",
          });
        }
      }
      emitUpdateCount();
    };

    const handleVideoFiles = async (files: FileList) => {
      const maxVideoSize = 50 * 1024 * 1024;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith("video/")) {
          if (file.size > maxVideoSize) {
            alert(`El video ${file.name} excede el tamaño máximo de 50MB`);
            continue;
          }
          const base64 = await blobToDataUrl(file);
          videos.value.push({
            file: file,
            filename: file.name,
            base64: base64,
            missatge: "",
            signatura: "",
          });
        }
      }
      emitUpdateCount();
    };

    const blobToDataUrl = (blob: File): Promise<string> =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

    const base64ToBlob = (base64: string): Blob => {
      const byteCharacters = atob(base64.split(",")[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: "image/jpeg" });
    };

    const rotateImage = (index: number): Promise<string> =>
      new Promise((resolve, reject) => {
        const imgEntry = images.value[index];
        const img = new Image();
        img.onload = () => {
          const w = img.width;
          const h = img.height;
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject("No canvas context");

          const angleRad = (imgEntry.rotation * Math.PI) / 180;

          if (imgEntry.rotation % 180 === 0) {
            canvas.width = w;
            canvas.height = h;
          } else {
            canvas.width = h;
            canvas.height = w;
          }

          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate(angleRad);
          ctx.drawImage(img, -w / 2, -h / 2);
          ctx.restore();

          const rotatedBase64 = canvas.toDataURL("image/jpeg", 0.95);
          const cleanBase64 = rotatedBase64.replace(
            /^data:image\/jpeg/,
            "data:image/png",
          );

          imgEntry.base64 = cleanBase64;
          imgEntry.originalBase64 = cleanBase64;
          resolve(cleanBase64);
        };
        img.onerror = reject;
        img.src = imgEntry.originalBase64;
      });

    const rotateLeft = (index: number) => {
      images.value[index].rotation =
        (images.value[index].rotation - 90 + 360) % 360;
      rotateImage(index);
    };

    const removeImage = (index: number) => {
      images.value.splice(index, 1);
      emitUpdateCount();
    };

    const removeVideo = (index: number) => {
      videos.value.splice(index, 1);
      emitUpdateCount();
    };

    // Text methods
    const addTextEntry = () => {
      texts.value.push({
        missatge: "",
        signatura: "",
      });
      emitUpdateCount();
    };

    const removeText = (index: number) => {
      texts.value.splice(index, 1);
      emitUpdateCount();
    };

    const emitUpdateCount = () => {
      emit(
        "update:count",
        images.value.length + videos.value.length + texts.value.length,
      );
    };

    const changeImages = async (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files || files.length === 0) {
        return;
      }
      await handleFiles(files);
      if (imgInputRef.value) {
        imgInputRef.value.value = "";
      }
    };

    const changeVideos = async (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files || files.length === 0) {
        return;
      }
      await handleVideoFiles(files);
      if (videoInputRef.value) {
        videoInputRef.value.value = "";
      }
    };

    const uploadAll = async (isVisible: boolean) => {
      const totalCount =
        images.value.length + videos.value.length + texts.value.length;
      if (totalCount === 0) {
        throw new Error("No hi ha cap contingut seleccionat");
      }

      const results = [];

      // Upload images
      for (const img of images.value) {
        const form = new FormData();

        // Si hay rotación, usar la imagen ya procesada del base64
        // Si no hay rotación, usar el archivo original
        let fileToUpload: File | Blob;
        if (img.rotation !== 0) {
          fileToUpload = base64ToBlob(img.base64);
        } else {
          fileToUpload = img.file;
        }

        form.append("id_vetlla", props.idVetlla.toString());
        form.append("missatge", img.missatge);
        form.append("signatura", img.signatura);
        form.append("is_visible", isVisible.toString());
        form.append("foto", fileToUpload);

        const res = await fetch("/api/diapositiva/create", {
          method: "POST",
          body: form,
        });

        if (!res.ok) {
          const text = await res.text();
          console.error("Error resposta:", text);
          throw new Error(text);
        }

        results.push(await res.json());
      }

      // Upload videos
      for (const video of videos.value) {
        const form = new FormData();

        form.append("id_vetlla", props.idVetlla.toString());
        form.append("missatge", video.missatge);
        form.append("signatura", video.signatura);
        form.append("is_visible", isVisible.toString());
        form.append("video", video.file);

        const res = await fetch("/api/diapositiva/create", {
          method: "POST",
          body: form,
        });

        if (!res.ok) {
          const text = await res.text();
          console.error("Error resposta video:", text);
          throw new Error(text);
        }

        results.push(await res.json());
      }

      // Upload text-only entries
      for (const text of texts.value) {
        const form = new FormData();
        form.append("id_vetlla", props.idVetlla.toString());
        form.append("missatge", text.missatge);
        form.append("signatura", text.signatura);
        form.append("is_visible", isVisible.toString());

        const res = await fetch("/api/diapositiva/create", {
          method: "POST",
          body: form,
        });

        if (!res.ok) {
          const textError = await res.text();
          console.error("Error resposta:", textError);
          throw new Error(textError);
        }

        results.push(await res.json());
      }

      return results;
    };

    const getTotalCount = () => {
      return images.value.length + videos.value.length + texts.value.length;
    };

    return {
      imgInputRef,
      videoInputRef,
      changeImages,
      changeVideos,
      uploadAll,
      removeImage,
      removeVideo,
      removeText,
      rotateLeft,
      images,
      videos,
      texts,
      triggerFileInput,
      triggerVideoInput,
      getTotalCount,
      isDragging,
      isDraggingVideo,
      onDragOver,
      onDragLeave,
      onDragOverVideo,
      onDragLeaveVideo,
      onDrop,
      onDropVideo,
      addTextEntry,
    };
  },
});
</script>

<style lang="scss">
textarea.form-input {
  height: auto;
  resize: vertical;
  min-height: 4.5rem;
  font-family: inherit;
  font-size: inherit;
  line-height: 1.4;
}

.media-input {
  cursor: pointer;
}

.section {
  margin-bottom: 2rem;

  &__title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1rem;
    color: $text-color;
  }
}

.drop-zone {
  border: 2px dashed #ccc;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  background: #fafafa;
  margin-bottom: 1rem;
  cursor: pointer;

  &:hover {
    border-color: $primary-color;
    background: #f5f5f5;
  }

  &--dragover {
    border-color: $primary-color;
    background: #e8f4e8;
    transform: scale(1.02);
  }

  &--text {
    background: #f0f7ff;

    &:hover {
      background: #e5f0ff;
    }
  }

  &--video {
    background: #fff5f0;

    &:hover {
      background: #ffe8e0;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  &__icon {
    font-size: 3rem;
    color: $primary-color;
  }

  &__text {
    font-size: 1.2rem;
    font-weight: bold;
    margin: 0;
    color: $text-color;
  }

  &__hint {
    font-size: 0.9rem;
    color: #888;
    margin: 0;
  }
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.image-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f9f9f9;

  &__preview {
    flex-shrink: 0;
    width: 150px;
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__filename {
    font-weight: bold;
    margin: 0;
  }

  &__actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;

    button {
      padding: 0.5em 1em;
      background: #f0f0f0;
      border: 1px solid #ccc;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        background: #ddd;
      }
    }

    .remove-btn {
      background: #ffeaea;
      border-color: #ffcccc;
      color: #cc0000;

      &:hover {
        background: #ffd5d5;
      }
    }
  }
}

.text-item {
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f9f9f9;

  &__info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__ideas {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin: 0.25rem 0;

    .idea-tag {
      font-size: 0.8rem;
      color: #888;
      background: #f0f0f0;
      border-radius: 12px;
      padding: 0.2em 0.7em;
    }
  }

  &__actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;

    button {
      padding: 0.5em 1em;
      background: #f0f0f0;
      border: 1px solid #ccc;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        background: #ddd;
      }
    }

    .remove-btn {
      background: #ffeaea;
      border-color: #ffcccc;
      color: #cc0000;

      &:hover {
        background: #ffd5d5;
      }
    }
  }
}

.video-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f9f9f9;

  &__preview {
    flex-shrink: 0;
    width: 150px;
    position: relative;

    video {
      border-radius: 4px;
    }
  }

  &__badge {
    position: absolute;
    top: 4px;
    left: 4px;
    background: #ff6600;
    color: white;
    font-size: 0.7rem;
    font-weight: bold;
    padding: 2px 6px;
    border-radius: 3px;
  }

  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__filename {
    font-weight: bold;
    margin: 0;
  }

  &__actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;

    button {
      padding: 0.5em 1em;
      background: #f0f0f0;
      border: 1px solid #ccc;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        background: #ddd;
      }
    }

    .remove-btn {
      background: #ffeaea;
      border-color: #ffcccc;
      color: #cc0000;

      &:hover {
        background: #ffd5d5;
      }
    }
  }
}
</style>
